/**
 * Client for `/api/v1/ticket-sales-campaigns`. Request and response shapes are
 * the shared contract in `@bonsai/shared`, so they cannot drift from the API.
 */
import type {
  TicketSalesCampaign,
  TicketSalesCampaignCreateRequest,
  TicketSalesCampaignEditEvent,
  TicketSalesCampaignHistoryResponse,
  TicketSalesCampaignListResponse,
  TicketSalesCampaignPatchRequest,
  TicketSalesCampaignResponse,
  TicketSalesCampaignSortField,
  TicketSalesCampaignSortOrder,
} from '@bonsai/shared';

import { apiFetch } from '../lib/apiFetch';

const BASE = '/api/v1/ticket-sales-campaigns';

/**
 * A refused request. `status` says why: 400 is a validation failure with
 * `fieldErrors` keyed by field (`_` for the form as a whole), 403 a missing
 * permission, 404 a campaign that does not exist.
 */
export class TicketSalesCampaignApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly fieldErrors: Record<string, string> = {},
  ) {
    super(message);
    this.name = 'TicketSalesCampaignApiError';
  }
}

async function send(url: string, init?: RequestInit): Promise<Response> {
  const res = await apiFetch(url, init);
  if (res.ok) return res;
  const body = (await res.json().catch(() => ({}))) as {
    message?: string;
    fieldErrors?: Record<string, string>;
  };
  throw new TicketSalesCampaignApiError(
    res.status,
    body.message ?? `Request failed (${res.status})`,
    body.fieldErrors ?? {},
  );
}

const campaignUrl = (id: string) => `${BASE}/${encodeURIComponent(id)}`;

export interface CampaignListParams {
  limit?: number;
  offset?: number;
  sort?: TicketSalesCampaignSortField;
  order?: TicketSalesCampaignSortOrder;
}

export async function fetchTicketSalesCampaigns(
  params: CampaignListParams = {},
  signal?: AbortSignal,
): Promise<TicketSalesCampaignListResponse> {
  const qs = new URLSearchParams();
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  if (params.offset !== undefined) qs.set('offset', String(params.offset));
  if (params.sort) qs.set('sort', params.sort);
  if (params.order) qs.set('order', params.order);
  const query = qs.toString();
  const res = await send(query ? `${BASE}?${query}` : BASE, { signal });
  return (await res.json()) as TicketSalesCampaignListResponse;
}

export async function fetchTicketSalesCampaign(
  id: string,
  signal?: AbortSignal,
): Promise<TicketSalesCampaign> {
  const res = await send(campaignUrl(id), { signal });
  return ((await res.json()) as TicketSalesCampaignResponse).campaign;
}

export async function createTicketSalesCampaign(
  body: TicketSalesCampaignCreateRequest,
): Promise<TicketSalesCampaign> {
  const res = await send(BASE, { method: 'POST', body: JSON.stringify(body) });
  return ((await res.json()) as TicketSalesCampaignResponse).campaign;
}

export async function updateTicketSalesCampaign(
  id: string,
  patch: TicketSalesCampaignPatchRequest,
): Promise<TicketSalesCampaign> {
  const res = await send(campaignUrl(id), { method: 'PATCH', body: JSON.stringify(patch) });
  return ((await res.json()) as TicketSalesCampaignResponse).campaign;
}

export async function fetchTicketSalesCampaignHistory(
  id: string,
  signal?: AbortSignal,
): Promise<TicketSalesCampaignEditEvent[]> {
  const res = await send(`${campaignUrl(id)}/history`, { signal });
  return ((await res.json()) as TicketSalesCampaignHistoryResponse).events;
}
